import type {
  CellDataType,
  ColumnDataType,
  ColumnUpdater,
  DatabaseBlockModel,
  ViewBasicDataType,
} from '@blocksuite/affine-model';
import { WriterInfoProvider } from '@blocksuite/affine-shared/services';
import {
  insertPositionToIndex,
  type InsertToPosition,
} from '@blocksuite/affine-shared/utils';
import {
  generateKeyBetweenV2,
  generateNKeysBetween,
} from '@blocksuite/std/gfx';
import type { BlockModel } from '@blocksuite/store';

import { getCanvasPresentationStateAfterViewDelete } from '../canvas-presentation-state.js';

const metaKeys = [
  'meta:createdAt',
  'meta:createdBy',
  'meta:updatedAt',
  'meta:updatedBy',
] as const;

const isMetaSupported = (model: BlockModel): boolean => {
  return metaKeys.every(key => model.keys.includes(key));
};

export const touchRowMeta = (model: DatabaseBlockModel, rowId: string) => {
  const row = model.store.getBlock(rowId)?.model;
  if (!row || !isMetaSupported(row)) {
    return;
  }
  const writer = model.store.getOptional(WriterInfoProvider)?.getWriterInfo();
  const now = Date.now();
  (row.props as Record<string, unknown>)['meta:updatedAt'] = now;
  if (writer) {
    (row.props as Record<string, unknown>)['meta:updatedBy'] = writer.id;
    if (!(row.props as Record<string, unknown>)['meta:createdBy']) {
      (row.props as Record<string, unknown>)['meta:createdBy'] = writer.id;
    }
  }
  if (!(row.props as Record<string, unknown>)['meta:createdAt']) {
    (row.props as Record<string, unknown>)['meta:createdAt'] = now;
  }
};

export function addProperty(
  model: DatabaseBlockModel,
  position: InsertToPosition,
  column: Omit<ColumnDataType, 'id'> & {
    id?: string;
  }
): string {
  const id = column.id ?? model.store.workspace.idGenerator();
  if (model.props.columns.some(v => v.id === id)) {
    return id;
  }
  model.store.transact(() => {
    const col: ColumnDataType = {
      ...column,
      id,
    };
    const columns = model.props.columns.slice();
    columns.splice(insertPositionToIndex(position, columns), 0, col);
    model.props.columns = columns;
  });
  return id;
}

export function copyCellsByProperty(
  model: DatabaseBlockModel,
  fromId: ColumnDataType['id'],
  toId: ColumnDataType['id']
) {
  model.store.transact(() => {
    Object.keys(model.props.cells).forEach(rowId => {
      const cell = model.props.cells[rowId]?.[fromId];
      if (cell && model.props.cells[rowId]) {
        model.props.cells[rowId][toId] = {
          ...cell,
          columnId: toId,
        };
      }
    });
  });
}

export function deleteColumn(
  model: DatabaseBlockModel,
  columnId: ColumnDataType['id']
) {
  const index = model.props.columns.findIndex(v => v.id === columnId);
  if (index < 0) return;

  model.store.transact(() => {
    model.props.columns = model.props.columns.filter((_, i) => i !== index);
  });
}

export function deleteRows(model: DatabaseBlockModel, rowIds: string[]) {
  model.store.transact(() => {
    for (const rowId of rowIds) {
      delete model.props.cells[rowId];
    }
  });
}

export function deleteView(model: DatabaseBlockModel, id: string) {
  const orderedViews = getOrderedViews(model.props.views);
  const nextCanvasPresentation = getCanvasPresentationStateAfterViewDelete(
    model,
    id,
    orderedViews
  );
  model.store.captureSync();
  model.store.transact(() => {
    if (model.props.views.length <= 1) {
      return;
    }
    const index = model.props.views.findIndex(v => v.id === id);
    if (index >= 0) {
      model.props.views.splice(index, 1);
      if (nextCanvasPresentation) {
        model.props.canvasPresentation = nextCanvasPresentation;
      }
    }
  });
}

export function duplicateView(model: DatabaseBlockModel, id: string): string {
  const newId = model.store.workspace.idGenerator();
  model.store.transact(() => {
    ensureViewOrderKeys(model.props.views);
    const views = getOrderedViews(model.props.views);
    const index = views.findIndex(v => v.id === id);
    const view = views[index];
    if (view) {
      const next = views[index + 1];
      const duplicate = JSON.parse(
        JSON.stringify({
          ...view,
          id: newId,
          order: generateKeyBetweenV2(view.order ?? null, next?.order ?? null),
        })
      );
      model.props.views.push(duplicate);
    }
  });
  return newId;
}

export function getOrderedViews<View extends ViewBasicDataType>(
  views: readonly View[]
): View[] {
  return views
    .map((view, index) => ({ index, view }))
    .sort((left, right) => {
      const leftOrder = left.view.order;
      const rightOrder = right.view.order;
      if (leftOrder && rightOrder) {
        if (leftOrder !== rightOrder) return leftOrder < rightOrder ? -1 : 1;
        if (left.view.id === right.view.id) return 0;
        return left.view.id < right.view.id ? -1 : 1;
      }
      if (leftOrder) return -1;
      if (rightOrder) return 1;
      return left.index - right.index;
    })
    .map(({ view }) => view);
}

function ensureViewOrderKeys(views: readonly ViewBasicDataType[]): void {
  if (views.every(view => view.order)) return;

  const keys = generateNKeysBetween(null, null, views.length);
  for (const [index, view] of views.entries()) {
    view.order = keys[index];
  }
}

type OrderedViewColumn = Record<string, unknown> & {
  id: string;
  order?: string;
};

function isOrderedViewColumn(value: unknown): value is OrderedViewColumn {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { id?: unknown }).id === 'string'
  );
}

function getOrderedViewColumns(columns: readonly OrderedViewColumn[]) {
  return columns.slice().sort((left, right) => {
    if (!left.order || !right.order) return 0;
    if (left.order !== right.order) return left.order < right.order ? -1 : 1;
    if (left.id === right.id) return 0;
    return left.id < right.id ? -1 : 1;
  });
}

function ensureViewColumnOrderKeys(columns: readonly OrderedViewColumn[]) {
  if (columns.every(column => column.order)) return;
  const keys = generateNKeysBetween(null, null, columns.length);
  for (const [index, column] of columns.entries()) {
    column.order = keys[index];
  }
}

function reconcileViewColumns(
  currentValue: unknown,
  nextValue: unknown
): boolean {
  if (
    !Array.isArray(currentValue) ||
    !Array.isArray(nextValue) ||
    !currentValue.every(isOrderedViewColumn) ||
    !nextValue.every(isOrderedViewColumn)
  ) {
    return false;
  }

  ensureViewColumnOrderKeys(currentValue);
  const currentById = new Map(currentValue.map(column => [column.id, column]));
  const nextIds = new Set(nextValue.map(column => column.id));

  for (let index = currentValue.length - 1; index >= 0; index--) {
    const column = currentValue[index];
    if (column && !nextIds.has(column.id)) currentValue.splice(index, 1);
  }

  for (const nextColumn of nextValue) {
    let currentColumn = currentById.get(nextColumn.id);
    if (!currentColumn) {
      currentValue.push({ ...nextColumn });
      currentColumn = currentValue.at(-1);
      if (!currentColumn) continue;
      currentById.set(nextColumn.id, currentColumn);
    }
    for (const key of Object.keys(currentColumn)) {
      if (key !== 'id' && key !== 'order' && !(key in nextColumn)) {
        delete currentColumn[key];
      }
    }
    for (const [key, value] of Object.entries(nextColumn)) {
      if (key !== 'order' && !Object.is(currentColumn[key], value)) {
        currentColumn[key] = value;
      }
    }
  }

  const hasMissingOrder = currentValue.some(column => !column.order);
  const currentOrder = getOrderedViewColumns(currentValue).map(({ id }) => id);
  const nextOrder = nextValue.map(({ id }) => id);
  if (
    hasMissingOrder ||
    currentOrder.some((id, index) => id !== nextOrder[index])
  ) {
    const keys = generateNKeysBetween(null, null, nextValue.length);
    for (const [index, id] of nextOrder.entries()) {
      const column = currentById.get(id);
      if (column) column.order = keys[index];
    }
  }
  return true;
}

export function getViewDataForRead<View extends ViewBasicDataType>(
  view: View
): View {
  const columns = (view as { columns?: unknown }).columns;
  if (
    !Array.isArray(columns) ||
    !columns.every(isOrderedViewColumn) ||
    !columns.some(column => column.order)
  ) {
    return view;
  }
  return {
    ...view,
    columns: getOrderedViewColumns(columns),
  } as View;
}

export function addView<View extends ViewBasicDataType>(
  model: DatabaseBlockModel,
  view: View
): void {
  model.store.transact(() => {
    ensureViewOrderKeys(model.props.views);
    const last = getOrderedViews(model.props.views).at(-1);
    model.props.views.push({
      ...view,
      order: generateKeyBetweenV2(last?.order ?? null, null),
    });
  });
}

export function getCell(
  model: DatabaseBlockModel,
  rowId: BlockModel['id'],
  columnId: ColumnDataType['id']
): CellDataType | null {
  if (columnId === 'title') {
    return {
      columnId: 'title',
      value: rowId,
    };
  }
  const yRow = model.props.cells$.value[rowId];
  const yCell = yRow?.[columnId] ?? null;
  if (!yCell) return null;

  return {
    columnId: yCell.columnId,
    value: yCell.value,
  };
}

export function getProperty(
  model: DatabaseBlockModel,
  id: ColumnDataType['id']
): ColumnDataType | undefined {
  return model.props.columns.find(v => v.id === id);
}

export function moveViewTo(
  model: DatabaseBlockModel,
  id: string,
  position: InsertToPosition
) {
  model.store.transact(() => {
    ensureViewOrderKeys(model.props.views);
    const views = getOrderedViews(model.props.views);
    const currentIndex = views.findIndex(v => v.id === id);
    if (currentIndex < 0) return;
    const requestedIndex = insertPositionToIndex(position, views);
    const nextIndex =
      currentIndex < requestedIndex ? requestedIndex - 1 : requestedIndex;
    if (currentIndex === nextIndex) return;

    const [view] = views.splice(currentIndex, 1);
    if (!view) return;
    view.order = generateKeyBetweenV2(
      views[nextIndex - 1]?.order ?? null,
      views[nextIndex]?.order ?? null
    );
  });
}

export function updateCell(
  model: DatabaseBlockModel,
  rowId: string,
  cell: CellDataType
) {
  model.store.transact(() => {
    const columnId = cell.columnId;
    if (
      rowId === '__proto__' ||
      rowId === 'constructor' ||
      rowId === 'prototype'
    ) {
      console.error('Invalid rowId');
      return;
    }
    if (
      columnId === '__proto__' ||
      columnId === 'constructor' ||
      columnId === 'prototype'
    ) {
      console.error('Invalid columnId');
      return;
    }
    if (!model.props.cells[rowId]) {
      model.props.cells[rowId] = Object.create(null);
    }
    if (model.props.cells[rowId]) {
      model.props.cells[rowId][columnId] = {
        columnId: columnId,
        value: cell.value,
      };
    }
    touchRowMeta(model, rowId);
  });
}

export function updateCells(
  model: DatabaseBlockModel,
  columnId: string,
  cells: Record<string, unknown>
) {
  model.store.transact(() => {
    Object.entries(cells).forEach(([rowId, value]) => {
      if (
        rowId === '__proto__' ||
        rowId === 'constructor' ||
        rowId === 'prototype'
      ) {
        throw new Error('Invalid rowId');
      }
      if (!model.props.cells[rowId]) {
        model.props.cells[rowId] = Object.create(null);
      }
      if (model.props.cells[rowId]) {
        model.props.cells[rowId][columnId] = {
          columnId,
          value,
        };
      }
      touchRowMeta(model, rowId);
    });
  });
}

export function updateProperty(
  model: DatabaseBlockModel,
  id: string,
  updater: ColumnUpdater,
  defaultValue?: Record<string, unknown>
) {
  const index = model.props.columns.findIndex(v => v.id === id);
  if (index == null) {
    return;
  }
  model.store.transact(() => {
    const column = model.props.columns[index];
    if (!column) {
      return;
    }
    const result = updater(column);
    model.props.columns = model.props.columns.map((currentColumn, i) =>
      i === index ? { ...defaultValue, ...column, ...result } : currentColumn
    );
  });
  return id;
}

export const updateView = <ViewData extends ViewBasicDataType>(
  model: DatabaseBlockModel,
  id: string,
  update: (data: ViewData) => Partial<ViewData>
) => {
  model.store.transact(() => {
    const index = model.props.views.findIndex(v => v.id === id);
    if (index < 0) return;
    const view = model.props.views[index] as ViewData | undefined;
    if (!view) return;
    const result = update(getViewDataForRead(view));
    const viewRecord = view as Record<string, unknown>;
    for (const [key, value] of Object.entries(result)) {
      if (value === undefined) {
        delete viewRecord[key];
      } else if (
        key === 'columns' &&
        reconcileViewColumns(viewRecord[key], value)
      ) {
        continue;
      } else if (!Object.is(viewRecord[key], value)) {
        viewRecord[key] = value;
      }
    }
  });
};
export const DATABASE_CONVERT_WHITE_LIST = ['affine:list', 'affine:paragraph'];
